import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../services/api'
import { Loader } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const STATUS_FA: Record<string, string> = {
  PENDING: 'در انتظار',
  CONFIRMED: 'تایید شده',
  PROCESSING: 'در حال پردازش',
  COMPLETED: 'تکمیل شده',
  CANCELLED: 'لغو شده'
}

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-gold-100 text-gold-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800'
}

interface ItemRow {
  id: string
  quantity: number
  unitPrice: number
  totalPrice: number
  product?: { name: string; type: 'GOLD' | 'SILVER'; weight: number; purity: number }
}

interface OrderRow {
  id: string
  orderNumber: string
  type: 'BUY' | 'SELL'
  status: string
  total: number
  createdAt: string
  user?: { firstName: string; lastName: string; email: string }
  items: ItemRow[]
}

const gramsOf = (order: OrderRow) =>
  order.items.reduce((sum, i) => sum + (i.product?.weight || 0) * i.quantity, 0)

const goldGramsOf = (order: OrderRow) =>
  order.items
    .filter((i) => i.product?.type === 'GOLD')
    .reduce((sum, i) => sum + (i.product?.weight || 0) * i.quantity, 0)

const silverGramsOf = (order: OrderRow) =>
  order.items
    .filter((i) => i.product?.type === 'SILVER')
    .reduce((sum, i) => sum + (i.product?.weight || 0) * i.quantity, 0)

/**
 * یک پنل کامل برای یک سمت معامله (خرید یا فروش) با جزئیات همه اقلام
 */
const SidePanel: React.FC<{
  title: string
  subtitle: string
  hint: string
  accent: 'green' | 'gold'
  orders: OrderRow[]
  loading: boolean
}> = ({ title, subtitle, hint, accent, orders, loading }) => {
  const totalAmount = orders.reduce((s, o) => s + o.total, 0)
  const totalGold = orders.reduce((s, o) => s + goldGramsOf(o), 0)
  const totalSilver = orders.reduce((s, o) => s + silverGramsOf(o), 0)
  const totalGrams = totalGold + totalSilver

  const headerCls = accent === 'green' ? 'bg-green-600' : 'bg-gold-600'

  return (
    <div className="card overflow-hidden flex flex-col">
      <div className={`${headerCls} text-white px-5 py-4`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="font-black">{title}</h2>
            <p className="text-[11px] opacity-90 mt-0.5">{subtitle}</p>
          </div>
          <span className="bg-white/20 rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap">
            {toFaDigits(orders.length)} سفارش
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 divide-x divide-x-reverse divide-gray-100 border-b border-gray-100 text-center">
        <div className="px-3 py-3">
          <div className="text-[11px] text-gray-500">جمع مبلغ</div>
          <div className="font-black text-sm text-gray-900 mt-0.5">{formatToman(totalAmount)}</div>
        </div>
        <div className="px-3 py-3">
          <div className="text-[11px] text-gray-500">جمع وزن</div>
          <div className="font-black text-sm text-gray-900 mt-0.5">{toFaDigits(totalGrams)} گرم</div>
        </div>
        <div className="px-3 py-3">
          <div className="text-[11px] text-gray-500">طلا / نقره</div>
          <div className="font-bold text-xs text-gray-700 mt-0.5">
            {toFaDigits(totalGold)} / {toFaDigits(totalSilver)}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-h-[65vh] divide-y divide-gray-100">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader /></div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">سفارشی در این بخش ثبت نشده است</div>
        ) : (
          orders.map((o) => {
            const grams = gramsOf(o)
            return (
              <div key={o.id} className="p-4 hover:bg-gray-50/60 transition-colors">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <Link to={`/orders/${o.id}`} className="font-black text-primary-600 hover:underline" dir="ltr">
                    {o.orderNumber}
                  </Link>
                  <span className={`badge ${STATUS_STYLE[o.status] || 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_FA[o.status] || o.status}
                  </span>
                </div>

                <div className="text-xs text-gray-500 mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    👤 {o.user?.firstName} {o.user?.lastName}
                  </span>
                  <span dir="ltr" className="text-[10px] text-gray-400">{o.user?.email}</span>
                  <span>🗓 {formatDateTimeFa(o.createdAt)}</span>
                </div>

                <table className="w-full text-xs mb-2">
                  <thead>
                    <tr className="text-gray-400 text-right">
                      <th className="font-normal pb-1">کالا</th>
                      <th className="font-normal pb-1 text-center">تعداد</th>
                      <th className="font-normal pb-1 text-center">وزن (گرم)</th>
                      <th className="font-normal pb-1 text-left">قیمت واحد</th>
                      <th className="font-normal pb-1 text-left">جمع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((i) => (
                      <tr key={i.id} className="border-t border-gray-50">
                        <td className="py-1.5 font-medium text-gray-800">{i.product?.name || '—'}</td>
                        <td className="py-1.5 text-center">{toFaDigits(i.quantity)}</td>
                        <td className="py-1.5 text-center text-gray-600">
                          {toFaDigits((i.product?.weight || 0) * i.quantity)}
                        </td>
                        <td className="py-1.5 text-left text-gray-600 whitespace-nowrap">{formatToman(i.unitPrice)}</td>
                        <td className="py-1.5 text-left font-bold text-gray-900 whitespace-nowrap">
                          {formatToman(i.totalPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-xs">
                  <span className="text-gray-600">
                    وزن کل: <b className="text-gray-900">{toFaDigits(grams)} گرم</b>
                  </span>
                  <span className="text-gray-600">
                    مبلغ کل: <b className="text-gray-900">{formatToman(o.total)}</b>
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">{hint}</div>
    </div>
  )
}

/**
 * صفحه «خرید و فروش»
 * لیست کامل سفارش‌های خرید و فروش، به‌صورت دو قسمت جدا ولی کنار هم
 */
const Trades: React.FC = () => {
  const [buyOrders, setBuyOrders] = useState<OrderRow[]>([])
  const [sellOrders, setSellOrders] = useState<OrderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { status: statusFilter || undefined, limit: 100 }
      const [buyRes, sellRes] = await Promise.all([
        adminApi.getOrders({ ...params, type: 'BUY' }),
        adminApi.getOrders({ ...params, type: 'SELL' })
      ])
      setBuyOrders(buyRes.data.data?.orders ?? [])
      setSellOrders(sellRes.data.data?.orders ?? [])
    } catch {
      toast.error('خطا در دریافت سفارش‌ها')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">خرید و فروش</h1>
          <p className="text-sm text-gray-500 mt-1">
            لیست کل سفارش‌های خرید و فروش، دو قسمت جدا و کنار هم با جزئیات کامل هر معامله
          </p>
        </div>
        <select
          className="input !w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(STATUS_FA).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* دو قسمت جدا، کنار هم */}
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <SidePanel
          title="سفارش‌های خرید مشتری"
          subtitle="مشتری از ما خریده — ما فلز فروخته‌ایم و پول گرفته‌ایم"
          hint="این سفارش‌ها پول را وارد صندوق می‌کنند و فلز از موجودی ما کم می‌کنند."
          accent="green"
          orders={buyOrders}
          loading={loading}
        />
        <SidePanel
          title="سفارش‌های فروش مشتری"
          subtitle="مشتری به ما فروخته — ما فلز خریده‌ایم و پول داده‌ایم"
          hint="این سفارش‌ها پول را از صندوق خارج می‌کنند و فلز به موجودی ما اضافه می‌کنند."
          accent="gold"
          orders={sellOrders}
          loading={loading}
        />
      </div>
    </div>
  )
}

export default Trades
